import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { wishlistService } from '../../services/wishlistService';
import { trackProfileUpdate } from '../../services/monEspaceAnalyticsService';

// ─── Shared sub-components ────────────────────────────────────────────────────

const SectionCard = ({ icon, iconBg = 'bg-brand-earth/10', iconColor = 'text-brand-earth', title, subtitle, children }) => (
  <div className="bg-white rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center space-x-3 px-5 py-4 border-b border-border/40">
      <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon name={icon} size={18} className={iconColor} />
      </div>
      <div>
        <p className="text-ios-callout font-body-semibold text-text-primary">{title}</p>
        {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

const SaveButton = ({ saving, saved, onClick, label = 'Sauvegarder' }) => (
  <div className="flex items-center justify-end space-x-3 pt-2">
    {saved && (
      <span className="flex items-center space-x-1.5 text-ios-caption1 text-green-600 bg-green-50 px-3 py-1.5 rounded-xl border border-green-100">
        <Icon name="CheckCircle" size={13} />
        <span>Sauvegardé</span>
      </span>
    )}
    <button
      onClick={onClick}
      disabled={saving}
      className="flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 disabled:opacity-60 transition-all duration-200 shadow-ios-light"
    >
      {saving ? (
        <>
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          <span>Sauvegarde…</span>
        </>
      ) : (
        <>
          <Icon name="Save" size={15} color="white" />
          <span>{label}</span>
        </>
      )}
    </button>
  </div>
);

const FieldInput = ({ label, type = 'text', value, onChange, placeholder, hint, disabled }) => (
  <div>
    <label className="block text-ios-callout font-body-medium text-text-primary mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full px-4 py-3 border border-border rounded-xl text-ios-callout text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent disabled:bg-surface/60 disabled:text-text-secondary transition-all duration-200"
    />
    {hint && <p className="text-ios-caption1 text-text-secondary mt-1">{hint}</p>}
  </div>
);

// ─── Profile Section ──────────────────────────────────────────────────────────

const ProfileSection = ({ user, userProfile, onProfileUpdated }) => {
  const [form, setForm] = useState({ fullName: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setForm({
      fullName: userProfile?.full_name || user?.user_metadata?.full_name || '',
      phone: userProfile?.phone || '',
    });
  }, [userProfile, user]);

  const handleSave = async () => {
    if (!user?.id) return;
    setError('');
    setSaving(true);
    try {
      const { error: profileErr } = await supabase?.from('user_profiles')?.update({ full_name: form?.fullName?.trim(), phone: form?.phone?.trim(), updated_at: new Date()?.toISOString() })?.eq('id', user?.id);

      if (profileErr) { setError(profileErr?.message); return; }

      // Also update auth metadata
      await supabase?.auth?.updateUser({ data: { full_name: form?.fullName?.trim() } });

      trackProfileUpdate('profile_info');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      onProfileUpdated?.();
    } catch (err) {
      setError('Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard icon="User" title="Informations du profil" subtitle="Nom, email et téléphone">
      <div className="space-y-4">
        <FieldInput
          label="Nom complet"
          value={form?.fullName}
          onChange={e => setForm(f => ({ ...f, fullName: e?.target?.value }))}
          placeholder="Votre nom complet"
        />
        <FieldInput
          label="Adresse email"
          type="email"
          value={user?.email || ''}
          disabled
          onChange={() => {}}
          placeholder=""
          hint="L'email ne peut pas être modifié ici. Utilisez la section vérification ci-dessous."
        />
        <FieldInput
          label="Numéro de téléphone"
          type="tel"
          value={form?.phone}
          onChange={e => setForm(f => ({ ...f, phone: e?.target?.value }))}
          placeholder="+225 07 00 00 00 00"
        />
        {error && (
          <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100">{error}</p>
        )}
        <SaveButton saving={saving} saved={saved} onClick={handleSave} />
      </div>
    </SectionCard>
  );
};

// ─── Password Section ─────────────────────────────────────────────────────────

const PasswordSection = () => {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (!form?.next || form?.next?.length < 8) {
      setError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (form?.next !== form?.confirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    setSaving(true);
    try {
      const { error: pwErr } = await supabase?.auth?.updateUser({ password: form?.next });
      if (pwErr) { setError(pwErr?.message); return; }
      setForm({ current: '', next: '', confirm: '' });
      trackProfileUpdate('password');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError('Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <SectionCard icon="Lock" iconBg="bg-amber-50" iconColor="text-amber-600" title="Changer le mot de passe" subtitle="Sécurisez votre compte">
      <div className="space-y-4">
        <FieldInput
          label="Nouveau mot de passe"
          type="password"
          value={form?.next}
          onChange={e => setForm(f => ({ ...f, next: e?.target?.value }))}
          placeholder="Minimum 8 caractères"
        />
        <FieldInput
          label="Confirmer le nouveau mot de passe"
          type="password"
          value={form?.confirm}
          onChange={e => setForm(f => ({ ...f, confirm: e?.target?.value }))}
          placeholder="Répétez le mot de passe"
        />
        {error && (
          <p className="text-ios-caption1 text-red-600 bg-red-50 px-3 py-2 rounded-xl border border-red-100">{error}</p>
        )}
        <SaveButton saving={saving} saved={saved} onClick={handleSave} label="Mettre à jour" />
      </div>
    </SectionCard>
  );
};

// ─── Verification Section ─────────────────────────────────────────────────────

const EDGE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-transactional-emails`;

const generateOtp = () => String(Math.floor(100000 + Math.random() * 900000));

const VerificationSection = ({ user, userProfile }) => {
  const [phoneSent, setPhoneSent] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // OTP state
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpExpiry, setOtpExpiry] = useState(null);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpVerifyLoading, setOtpVerifyLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const isEmailVerified = emailVerified || !!user?.email_confirmed_at;
  const currentPhone = userProfile?.phone || '';

  // Countdown timer for OTP expiry
  useEffect(() => {
    if (!otpExpiry) return;
    const interval = setInterval(() => {
      const remaining = Math.max(0, Math.floor((otpExpiry - Date.now()) / 1000));
      setCountdown(remaining);
      if (remaining === 0) {
        clearInterval(interval);
        setOtpSent(false);
        setOtpCode('');
        setOtpInput('');
        setOtpError('Le code a expiré. Veuillez en demander un nouveau.');
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [otpExpiry]);

  const handleSendOtp = async () => {
    setOtpError('');
    setOtpLoading(true);
    try {
      const code = generateOtp();
      const expiry = Date.now() + 10 * 60 * 1000; // 10 minutes

      const { data: sessionData } = await supabase?.auth?.getSession();
      const accessToken = sessionData?.session?.access_token;

      const response = await fetch(EDGE_FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({
          type: 'email_otp_verification',
          data: {
            user_email: user?.email,
            otp_code: code,
            user_name: userProfile?.full_name || user?.user_metadata?.full_name || '',
          },
        }),
      });

      if (!response?.ok) {
        const err = await response?.json()?.catch(() => ({}));
        setOtpError(err?.error || 'Impossible d\'envoyer le code. Réessayez.');
        return;
      }

      setOtpCode(code);
      setOtpExpiry(expiry);
      setOtpSent(true);
      setOtpInput('');
      setCountdown(600);
    } catch (err) {
      setOtpError('Une erreur est survenue lors de l\'envoi du code.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = () => {
    setOtpError('');
    if (!otpInput || otpInput?.trim() !== otpCode) {
      setOtpError('Code incorrect. Vérifiez et réessayez.');
      return;
    }
    if (Date.now() > otpExpiry) {
      setOtpError('Le code a expiré. Veuillez en demander un nouveau.');
      setOtpSent(false);
      return;
    }
    setOtpVerifyLoading(true);
    setTimeout(() => {
      setEmailVerified(true);
      setOtpSent(false);
      setOtpCode('');
      setOtpInput('');
      setOtpExpiry(null);
      setOtpVerifyLoading(false);
    }, 600);
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s)?.padStart(2, '0')}`;
  };

  const handlePhoneVerify = async () => {
    const phone = newPhone || currentPhone;
    if (!phone || !/^\+?[\d\s\-()]{7,20}$/?.test(phone)) {
      setPhoneError('Numéro de téléphone invalide.');
      return;
    }
    setPhoneError('');
    setPhoneLoading(true);
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ phone: phone?.trim(), updated_at: new Date()?.toISOString() })?.eq('id', user?.id);
      if (error) { setPhoneError(error?.message); return; }
      setPhoneSent(true);
      setNewPhone('');
      setTimeout(() => setPhoneSent(false), 4000);
    } catch {
      setPhoneError('Une erreur est survenue.');
    } finally {
      setPhoneLoading(false);
    }
  };

  return (
    <SectionCard icon="ShieldCheck" iconBg="bg-green-50" iconColor="text-green-600" title="Vérification email & téléphone" subtitle="Confirmez vos coordonnées">
      <div className="space-y-6">
        {/* Email verification */}
        <div className="p-4 rounded-xl border border-border/50 bg-surface/30">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Icon name="Mail" size={16} className="text-text-secondary" />
              <span className="text-ios-callout font-body-medium text-text-primary">Email actuel</span>
            </div>
            <span className={`flex items-center space-x-1 text-ios-caption1 px-2.5 py-1 rounded-full font-body-medium ${isEmailVerified ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
              <Icon name={isEmailVerified ? 'CheckCircle' : 'AlertCircle'} size={11} />
              <span>{isEmailVerified ? 'Vérifié' : 'Non vérifié'}</span>
            </span>
          </div>
          <p className="text-ios-callout text-text-secondary mb-3">{user?.email}</p>

          {isEmailVerified ? (
            <div className="flex items-center space-x-2 text-ios-caption1 text-green-600 bg-green-50 px-3 py-2 rounded-xl border border-green-100">
              <Icon name="CheckCircle" size={13} />
              <span>Votre adresse email est vérifiée.</span>
            </div>
          ) : otpSent ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-ios-caption1 text-text-secondary">
                  Code envoyé à <strong className="text-text-primary">{user?.email}</strong>
                </p>
                <span className={`text-ios-caption1 font-body-medium px-2 py-0.5 rounded-full ${countdown > 60 ? 'text-green-600 bg-green-50' : 'text-amber-600 bg-amber-50'}`}>
                  {formatCountdown(countdown)}
                </span>
              </div>
              <input
                type="text"
                value={otpInput}
                onChange={e => setOtpInput(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
                placeholder="Entrez le code à 6 chiffres"
                maxLength={6}
                className="w-full px-4 py-2.5 border border-border rounded-xl text-ios-callout text-center tracking-[0.3em] font-body-semibold focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
              />
              {otpError && <p className="text-ios-caption1 text-red-600">{otpError}</p>}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleVerifyOtp}
                  disabled={otpVerifyLoading || otpInput?.length !== 6}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-xl text-ios-callout font-body-medium hover:bg-green-700 disabled:opacity-50 transition-all"
                >
                  {otpVerifyLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Icon name="ShieldCheck" size={14} color="white" />}
                  <span>Confirmer le code</span>
                </button>
                <button
                  onClick={() => { setOtpSent(false); setOtpInput(''); setOtpError(''); }}
                  className="px-3 py-2 text-ios-caption1 text-text-secondary hover:text-text-primary transition-colors"
                >
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {otpError && <p className="text-ios-caption1 text-red-600">{otpError}</p>}
              <button
                onClick={handleSendOtp}
                disabled={otpLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-brand-earth/10 text-brand-earth rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/20 disabled:opacity-50 transition-all"
              >
                {otpLoading ? <div className="w-4 h-4 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" /> : <Icon name="Mail" size={14} />}
                <span>Envoyer un code de vérification</span>
              </button>
            </div>
          )}
        </div>

        {/* Phone verification */}
        <div className="p-4 rounded-xl border border-border/50 bg-surface/30">
          <div className="flex items-center space-x-2 mb-3">
            <Icon name="Phone" size={16} className="text-text-secondary" />
            <span className="text-ios-callout font-body-medium text-text-primary">Téléphone</span>
          </div>
          {currentPhone && (
            <p className="text-ios-callout text-text-secondary mb-3">{currentPhone}</p>
          )}
          {phoneSent ? (
            <div className="flex items-center space-x-2 text-ios-caption1 text-green-600 bg-green-50 px-3 py-2 rounded-xl border border-green-100">
              <Icon name="CheckCircle" size={13} />
              <span>Numéro de téléphone mis à jour avec succès.</span>
            </div>
          ) : (
            <div className="space-y-2">
              <input
                type="tel"
                value={newPhone}
                onChange={e => setNewPhone(e?.target?.value)}
                placeholder={currentPhone || '+225 07 00 00 00 00'}
                className="w-full px-4 py-2.5 border border-border rounded-xl text-ios-callout focus:ring-2 focus:ring-brand-gold focus:border-transparent transition-all"
              />
              {phoneError && <p className="text-ios-caption1 text-red-600">{phoneError}</p>}
              <button
                onClick={handlePhoneVerify}
                disabled={phoneLoading}
                className="flex items-center space-x-2 px-4 py-2 bg-brand-earth/10 text-brand-earth rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/20 disabled:opacity-50 transition-all"
              >
                {phoneLoading ? <div className="w-4 h-4 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" /> : <Icon name="Phone" size={14} />}
                <span>{currentPhone ? 'Mettre à jour' : 'Ajouter le téléphone'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
};

// ─── Linked Devices Section ───────────────────────────────────────────────────

const LinkedDevicesSection = ({ user }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const loadDevices = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('linked_devices')?.select('*')?.eq('user_id', user?.id)?.order('last_seen_at', { ascending: false });
      if (!error) setDevices(data || []);
    } catch (err) {
      console.error('Error loading devices:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  // Register current device on mount
  useEffect(() => {
    if (!user?.id) return;
    const registerDevice = async () => {
      const ua = navigator.userAgent;
      const deviceName = /Mobile|Android|iPhone|iPad/i?.test(ua)
        ? 'Appareil mobile'
        : /Tablet/i?.test(ua) ? 'Tablette' : 'Ordinateur';
      const browser = /Chrome/i?.test(ua) ? 'Chrome' : /Firefox/i?.test(ua) ? 'Firefox' : /Safari/i?.test(ua) ? 'Safari' : 'Navigateur';
      const deviceFingerprint = btoa(`${ua}-${navigator.language}-${screen.width}x${screen.height}`)?.slice(0, 32);

      await supabase?.from('linked_devices')?.upsert({
        user_id: user?.id,
        device_fingerprint: deviceFingerprint,
        device_name: deviceName,
        browser,
        user_agent: ua?.slice(0, 255),
        last_seen_at: new Date()?.toISOString(),
        is_current: true,
      }, { onConflict: 'user_id,device_fingerprint' });

      // Mark all others as not current
      await supabase?.from('linked_devices')?.update({ is_current: false })?.eq('user_id', user?.id)?.neq('device_fingerprint', deviceFingerprint);

      loadDevices();
    };
    registerDevice();
  }, [user?.id, loadDevices]);

  const handleRevoke = async (deviceId) => {
    setRevoking(deviceId);
    try {
      await supabase?.from('linked_devices')?.delete()?.eq('id', deviceId);
      setDevices(d => d?.filter(dev => dev?.id !== deviceId));
    } catch (err) {
      console.error('Error revoking device:', err);
    } finally {
      setRevoking(null);
    }
  };

  const getDeviceIcon = (deviceName = '') => {
    if (/mobile|téléphone/i?.test(deviceName)) return 'Smartphone';
    if (/tablette/i?.test(deviceName)) return 'Tablet';
    return 'Monitor';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <SectionCard icon="Laptop" iconBg="bg-blue-50" iconColor="text-blue-600" title="Appareils liés" subtitle="Gérez les appareils connectés à votre compte">
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
        </div>
      ) : devices?.length === 0 ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-2xl bg-surface flex items-center justify-center mx-auto mb-3">
            <Icon name="Laptop" size={22} className="text-text-secondary" />
          </div>
          <p className="text-ios-callout text-text-secondary">Aucun appareil enregistré</p>
        </div>
      ) : (
        <div className="space-y-3">
          {devices?.map(device => (
            <div key={device?.id} className={`flex items-center justify-between p-4 rounded-xl border transition-all ${device?.is_current ? 'border-brand-gold/40 bg-brand-gold/5' : 'border-border/50 bg-surface/30'}`}>
              <div className="flex items-center space-x-3 min-w-0">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${device?.is_current ? 'bg-brand-gold/15' : 'bg-surface'}`}>
                  <Icon name={getDeviceIcon(device?.device_name)} size={18} className={device?.is_current ? 'text-brand-earth' : 'text-text-secondary'} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-ios-callout font-body-medium text-text-primary truncate">{device?.device_name || 'Appareil inconnu'}</p>
                    {device?.is_current && (
                      <span className="flex-shrink-0 text-[10px] font-body-semibold text-brand-earth bg-brand-gold/15 px-2 py-0.5 rounded-full">Actuel</span>
                    )}
                  </div>
                  <p className="text-ios-caption1 text-text-secondary truncate">{device?.browser} · Dernière activité : {formatDate(device?.last_seen_at)}</p>
                </div>
              </div>
              {!device?.is_current && (
                <button
                  onClick={() => handleRevoke(device?.id)}
                  disabled={revoking === device?.id}
                  className="flex-shrink-0 ml-3 flex items-center space-x-1.5 px-3 py-1.5 text-red-600 bg-red-50 hover:bg-red-100 rounded-xl text-ios-caption1 font-body-medium transition-all disabled:opacity-50"
                >
                  {revoking === device?.id ? (
                    <div className="w-3.5 h-3.5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="Trash2" size={13} />
                  )}
                  <span>Révoquer</span>
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ─── Wishlist Section ─────────────────────────────────────────────────────────

const COMMUNES_OPTIONS = [
  'all', 'Cocody', 'Plateau', 'Marcory', 'Treichville', 'Adjamé', 'Abobo',
  'Yopougon', 'Koumassi', 'Port-Bouët', 'Attécoubé', 'Bingerville', 'Songon',
];

const WishlistSection = ({ user }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [filters, setFilters] = useState({
    commune: 'all',
    minPrice: '',
    maxPrice: '',
    sortOrder: 'desc',
  });

  const loadWishlist = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await wishlistService?.getWishlist(user?.id, filters);
      setItems(data || []);
    } catch (err) {
      console.error('Error loading wishlist:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, filters]);

  useEffect(() => { loadWishlist(); }, [loadWishlist]);

  const handleRemove = async (propertyId) => {
    setRemoving(propertyId);
    try {
      await wishlistService?.removeFromWishlist(user?.id, propertyId);
      setItems(prev => prev?.filter(item => item?.property_id !== propertyId));
    } catch (err) {
      console.error('Error removing from wishlist:', err);
    } finally {
      setRemoving(null);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'Prix sur demande';
    if (price >= 1000000) return `${(price / 1000000)?.toFixed(1)?.replace(/\.0$/, '')}M XOF`;
    if (price >= 1000) return `${(price / 1000)?.toFixed(0)}K XOF`;
    return `${price?.toLocaleString()} XOF`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr)?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <SectionCard icon="Heart" iconBg="bg-rose-50" iconColor="text-rose-500" title="Mes Favoris" subtitle={`${items?.length} propriété${items?.length !== 1 ? 's' : ''} sauvegardée${items?.length !== 1 ? 's' : ''}`}>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5 pb-4 border-b border-border/40">
        {/* Commune filter */}
        <div className="flex-1 min-w-[140px]">
          <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1">Commune</label>
          <select
            value={filters?.commune}
            onChange={e => setFilters(f => ({ ...f, commune: e?.target?.value }))}
            className="w-full px-3 py-2 border border-border rounded-xl text-ios-caption1 text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white"
          >
            <option value="all">Toutes les communes</option>
            {COMMUNES_OPTIONS?.slice(1)?.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        {/* Min price */}
        <div className="flex-1 min-w-[120px]">
          <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1">Prix min (XOF)</label>
          <input
            type="number"
            value={filters?.minPrice}
            onChange={e => setFilters(f => ({ ...f, minPrice: e?.target?.value }))}
            placeholder="0"
            className="w-full px-3 py-2 border border-border rounded-xl text-ios-caption1 text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent"
          />
        </div>

        {/* Max price */}
        <div className="flex-1 min-w-[120px]">
          <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1">Prix max (XOF)</label>
          <input
            type="number"
            value={filters?.maxPrice}
            onChange={e => setFilters(f => ({ ...f, maxPrice: e?.target?.value }))}
            placeholder="Illimité"
            className="w-full px-3 py-2 border border-border rounded-xl text-ios-caption1 text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent"
          />
        </div>

        {/* Sort order */}
        <div className="flex-1 min-w-[140px]">
          <label className="block text-ios-caption1 font-body-medium text-text-secondary mb-1">Trier par date</label>
          <select
            value={filters?.sortOrder}
            onChange={e => setFilters(f => ({ ...f, sortOrder: e?.target?.value }))}
            className="w-full px-3 py-2 border border-border rounded-xl text-ios-caption1 text-text-primary focus:ring-2 focus:ring-brand-gold focus:border-transparent bg-white"
          >
            <option value="desc">Plus récent d'abord</option>
            <option value="asc">Plus ancien d'abord</option>
          </select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-brand-earth border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items?.length === 0 ? (
        <div className="text-center py-10">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-3">
            <Icon name="Heart" size={24} className="text-rose-400" />
          </div>
          <p className="text-ios-callout font-body-medium text-text-primary mb-1">Aucun favori</p>
          <p className="text-ios-caption1 text-text-secondary mb-4">Cliquez sur le cœur d'une propriété pour la sauvegarder ici.</p>
          <Link
            to="/property-search-listings"
            className="inline-flex items-center space-x-1.5 px-4 py-2 bg-brand-earth/10 text-brand-earth rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/20 transition-all"
          >
            <Icon name="Search" size={14} />
            <span>Explorer les propriétés</span>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {items?.map(item => (
            <div key={item?.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 bg-surface/30 hover:bg-surface/60 transition-all">
              {/* Thumbnail */}
              <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-surface">
                {item?.property_image ? (
                  <img
                    src={item?.property_image}
                    alt={item?.property_title || 'Propriété'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Home" size={20} className="text-text-secondary" />
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-ios-callout font-body-medium text-text-primary truncate">
                  {item?.property_title || 'Propriété sans titre'}
                </p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {item?.property_commune && (
                    <span className="flex items-center gap-0.5 text-ios-caption1 text-text-secondary">
                      <Icon name="MapPin" size={11} />
                      {item?.property_commune}
                    </span>
                  )}
                  <span className="text-ios-caption1 font-body-medium text-brand-earth">
                    {formatPrice(item?.property_price)}
                  </span>
                </div>
                <p className="text-[10px] text-text-secondary mt-0.5">
                  Ajouté le {formatDate(item?.created_at)}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Link
                  to={`/individual-property-page/${item?.property_id}`}
                  className="w-8 h-8 rounded-xl bg-brand-earth/10 flex items-center justify-center hover:bg-brand-earth/20 transition-all"
                  title="Voir la propriété"
                >
                  <Icon name="ExternalLink" size={14} className="text-brand-earth" />
                </Link>
                <button
                  onClick={() => handleRemove(item?.property_id)}
                  disabled={removing === item?.property_id}
                  className="w-8 h-8 rounded-xl bg-rose-50 flex items-center justify-center hover:bg-rose-100 transition-all disabled:opacity-50"
                  title="Retirer des favoris"
                >
                  {removing === item?.property_id ? (
                    <div className="w-3.5 h-3.5 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Icon name="HeartOff" size={14} className="text-rose-500" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const AccountDashboard = () => {
  const { user, userProfile, getUserProfile } = useAuth();

  const handleProfileUpdated = useCallback(() => {
    getUserProfile?.();
  }, [getUserProfile]);

  if (!user) {
    return (
      <div className="min-h-screen bg-surface/30">
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand-earth/10 flex items-center justify-center mx-auto mb-5">
            <Icon name="Lock" size={28} className="text-brand-earth" />
          </div>
          <h2 className="text-ios-title2 font-display font-display-semibold text-brand-earth mb-3">
            Connexion requise
          </h2>
          <p className="text-ios-callout text-text-secondary mb-6">
            Connectez-vous pour accéder à votre tableau de bord de compte.
          </p>
          <Link
            to="/homepage"
            className="inline-flex items-center space-x-2 px-5 py-2.5 bg-brand-earth text-white rounded-xl text-ios-callout font-body-medium hover:bg-brand-earth/90 transition-colors"
          >
            <Icon name="Home" size={16} color="white" />
            <span>Retour à l'accueil</span>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Mon Compte – Kapital States</title>
        <meta name="description" content="Gérez votre profil, mot de passe, vérification email/téléphone et appareils liés." />
      </Helmet>
      <div className="min-h-screen bg-surface/30">
        <Header />

        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">

          {/* Breadcrumb */}
          <div className="flex items-center space-x-2 text-ios-caption1 text-text-secondary mb-6">
            <Link to="/homepage" className="hover:text-brand-earth transition-colors">Accueil</Link>
            <Icon name="ChevronRight" size={12} />
            <span className="text-text-primary font-body-medium">Mon Compte</span>
          </div>

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-gold to-brand-earth flex items-center justify-center shadow-ios-light flex-shrink-0">
                <Icon name="UserCircle" size={28} color="white" />
              </div>
              <div>
                <h1 className="text-ios-title2 font-display font-display-semibold text-brand-earth leading-tight">
                  Mon Compte
                </h1>
                <p className="text-ios-callout text-text-secondary mt-0.5">
                  {userProfile?.full_name || user?.user_metadata?.full_name || user?.email}
                </p>
              </div>
            </div>

            {/* Quick nav tabs */}
            <div className="flex items-center space-x-2 mt-6 overflow-x-auto pb-1">
              {[
                { label: 'Profil', icon: 'User', href: '#profile' },
                { label: 'Mot de passe', icon: 'Lock', href: '#password' },
                { label: 'Vérification', icon: 'ShieldCheck', href: '#verification' },
                { label: 'Appareils', icon: 'Laptop', href: '#devices' },
                { label: 'Favoris', icon: 'Heart', href: '#wishlist' },
                { label: 'Communication', icon: 'MessageSquare', href: '#communication' },
              ]?.map(tab => (
                <a
                  key={tab?.href}
                  href={tab?.href}
                  className="flex items-center space-x-1.5 px-3.5 py-2 rounded-xl bg-white border border-border/50 text-ios-caption1 font-body-medium text-text-secondary hover:text-brand-earth hover:border-brand-gold/40 hover:bg-brand-gold/5 transition-all whitespace-nowrap shadow-ios-light"
                >
                  <Icon name={tab?.icon} size={13} />
                  <span>{tab?.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            <div id="profile">
              <ProfileSection user={user} userProfile={userProfile} onProfileUpdated={handleProfileUpdated} />
            </div>
            <div id="password">
              <PasswordSection />
            </div>
            <div id="verification">
              <VerificationSection user={user} userProfile={userProfile} />
            </div>
            <div id="devices">
              <LinkedDevicesSection user={user} />
            </div>
            <div id="wishlist">
              <WishlistSection user={user} />
            </div>
          </div>

          {/* Communication settings section */}
          <div id="communication" className="mt-6 space-y-3">
            <div className="p-4 rounded-2xl bg-white border border-border/50 shadow-ios-light flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-100 to-brand-earth/10 flex items-center justify-center">
                  <Icon name="MessageSquare" size={18} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-ios-callout font-body-medium text-text-primary">Paramètres de communication</p>
                  <p className="text-ios-caption1 text-text-secondary">Rappels, promotions et messages transactionnels</p>
                </div>
              </div>
              <Link
                to="/account-communication-settings"
                className="flex items-center space-x-1.5 px-4 py-2 bg-surface rounded-xl text-ios-callout font-body-medium text-brand-earth hover:bg-brand-gold/10 transition-all"
              >
                <span>Configurer</span>
                <Icon name="ChevronRight" size={14} />
              </Link>
            </div>

            {/* Footer link to notification preferences */}
            <div className="p-4 rounded-2xl bg-white border border-border/50 shadow-ios-light flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
                  <Icon name="Bell" size={18} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-ios-callout font-body-medium text-text-primary">Préférences de notifications</p>
                  <p className="text-ios-caption1 text-text-secondary">SMS, email et fréquence</p>
                </div>
              </div>
              <Link
                to="/notification-preferences"
                className="flex items-center space-x-1.5 px-4 py-2 bg-surface rounded-xl text-ios-callout font-body-medium text-brand-earth hover:bg-brand-gold/10 transition-all"
              >
                <span>Gérer</span>
                <Icon name="ChevronRight" size={14} />
              </Link>
            </div>

            {/* Email frequency & digest settings */}
            <div className="p-4 rounded-2xl bg-white border border-border/50 shadow-ios-light flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center">
                  <Icon name="Mail" size={18} className="text-teal-600" />
                </div>
                <div>
                  <p className="text-ios-callout font-body-medium text-text-primary">Paramètres email & digest</p>
                  <p className="text-ios-caption1 text-text-secondary">Fréquence, canaux et options anti-désabonnement</p>
                </div>
              </div>
              <Link
                to="/user-email-settings"
                className="flex items-center space-x-1.5 px-4 py-2 bg-surface rounded-xl text-ios-callout font-body-medium text-brand-earth hover:bg-brand-gold/10 transition-all"
              >
                <span>Configurer</span>
                <Icon name="ChevronRight" size={14} />
              </Link>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AccountDashboard;
